"use client";

import { useEffect, useRef } from "react";
import { useSession, signOut } from "next-auth/react";
import { useCurrentAccountId, useSsoHint } from "@zapier/identity";

/**
 * Custom hook that monitors for changes to the currentAccountId cookie
 * and automatically logs the user out if it changes or is deleted.
 */
export function useAccountIdMonitor() {
  const { data: session, status } = useSession();
  const currentAccountIdFromCookie = useCurrentAccountId();
  const ssoHint = useSsoHint();
  const isSigningOut = useRef(false);
  const isDevOrPreview =
    process.env.NODE_ENV === "development" ||
    process.env.NEXT_PUBLIC_VERCEL_ENV === "preview";

  // Monitor ssoHint for logout detection (becomes anonymous when logged out)
  useEffect(() => {
    // Skip in dev/preview environments
    if (isDevOrPreview) {
      return;
    }

    // Skip if not authenticated or already signing out
    if (status !== "authenticated" || isSigningOut.current) {
      return;
    }

    // If ssoHint is "anonymous", user has logged out
    if (ssoHint === "anonymous") {
      isSigningOut.current = true;
      signOut({ callbackUrl: "/" });
    }
  }, [ssoHint, status, isDevOrPreview]);

  // Monitor currentAccountId for account switching
  useEffect(() => {
    // Skip in dev/preview environments
    if (isDevOrPreview) {
      return;
    }

    // Skip if not authenticated or already signing out
    if (status !== "authenticated" || isSigningOut.current) {
      return;
    }

    // Get the authenticated account ID from the session
    const authenticatedAccountId = Number(session?.currentAccountId);

    // Skip if we don't have both IDs to compare
    if (!authenticatedAccountId || !currentAccountIdFromCookie) {
      return;
    }

    // Check if the account ID has changed (account switch)
    if (currentAccountIdFromCookie !== authenticatedAccountId) {
      isSigningOut.current = true;
      signOut({ callbackUrl: "/" });
    }
  }, [currentAccountIdFromCookie, session, status, isDevOrPreview]);

  return {
    currentAccountIdFromCookie,
    currentAccountIdFromSession: session?.currentAccountId,
    ssoHint,
  };
}
